import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './SearchModal.css';

interface Document {
  name: string;
  path: string;
  content?: string;
  size: number;
  modified: Date;
  created?: Date;
  gitStatus?: string | null;
  commits?: number;
  authors?: string[];
  links?: string[];
  tags?: string[];
}

interface SearchResult {
  document: Document;
  score: number;
  matches: {
    content?: string[];
    title?: boolean;
    tags?: string[];
  };
  preview?: string;
}

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  documents: Document[];
  currentPath?: string;
  onSelectDocument: (path: string) => void;
  gitHistory?: Map<string, any>;
}

type SearchCategory = 'all' | 'content' | 'name' | 'recent' | 'linked' | 'modified' | 'tags';
type TimeFilter = 'anytime' | 'today' | 'week' | 'month' | 'year';

const SearchModal: React.FC<SearchModalProps> = ({
  isOpen,
  onClose,
  documents,
  currentPath,
  onSelectDocument,
  gitHistory
}) => {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState<SearchCategory>('all');
  const [timeFilter, setTimeFilter] = useState<TimeFilter>('anytime');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [searchHistory, setSearchHistory] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Fuzzy search algorithm
  const fuzzyMatch = useCallback((str: string, pattern: string): number => {
    const lowerStr = str.toLowerCase();
    const lowerPattern = pattern.toLowerCase();
    
    if (lowerStr === lowerPattern) return 1.0;
    if (lowerStr.includes(lowerPattern)) return 0.8;
    
    let score = 0;
    let patternIdx = 0;
    let prevMatchIdx = -1;
    
    for (let i = 0; i < lowerStr.length && patternIdx < lowerPattern.length; i++) {
      if (lowerStr[i] === lowerPattern[patternIdx]) {
        score += (prevMatchIdx === -1 || i - prevMatchIdx === 1) ? 0.2 : 0.1;
        prevMatchIdx = i;
        patternIdx++;
      }
    }
    
    return patternIdx === lowerPattern.length ? Math.min(score, 1.0) : 0;
  }, []);

  // Extract preview text around matches
  const extractPreview = useCallback((content: string, query: string, maxLength = 150): string => {
    const lowerContent = content.toLowerCase();
    const lowerQuery = query.toLowerCase();
    const index = lowerContent.indexOf(lowerQuery);
    
    if (index === -1) return content.slice(0, maxLength) + '...';
    
    const start = Math.max(0, index - 50);
    const end = Math.min(content.length, index + query.length + 100);
    let preview = content.slice(start, end);
    
    if (start > 0) preview = '...' + preview;
    if (end < content.length) preview = preview + '...';
    
    return preview;
  }, []);

  // Time filter check
  const matchesTimeFilter = useCallback((doc: Document): boolean => {
    if (timeFilter === 'anytime') return true;
    
    const now = new Date();
    const modified = new Date(doc.modified);
    const diff = now.getTime() - modified.getTime();
    const days = diff / (1000 * 60 * 60 * 24);
    
    switch (timeFilter) {
      case 'today': return days < 1;
      case 'week': return days < 7;
      case 'month': return days < 30;
      case 'year': return days < 365;
      default: return true;
    }
  }, [timeFilter]);

  // Smart search suggestions
  const suggestions = useMemo(() => {
    if (!query || query.length < 2) return [];
    
    const docNames = documents.map(d => d.name);
    const uniqueTags = [...new Set(documents.flatMap(d => d.tags || []))];
    
    const nameSuggestions = docNames
      .filter(name => fuzzyMatch(name, query) > 0.3)
      .map(name => ({ type: 'document', value: name }));
    
    const tagSuggestions = uniqueTags
      .filter(tag => fuzzyMatch(tag, query) > 0.3)
      .map(tag => ({ type: 'tag', value: tag }));
    
    return [...nameSuggestions, ...tagSuggestions].slice(0, 5);
  }, [query, documents, fuzzyMatch]);

  // Main search function
  const performSearch = useCallback(() => {
    if (!query.trim() && category === 'all') {
      // Show recent documents when no query
      const recentDocs = documents
        .filter(matchesTimeFilter)
        .sort((a, b) => new Date(b.modified).getTime() - new Date(a.modified).getTime())
        .slice(0, 10)
        .map(doc => ({
          document: doc,
          score: 0.5,
          matches: {},
          preview: doc.content ? doc.content.slice(0, 150) + '...' : ''
        }));
      setResults(recentDocs);
      return;
    }

    const searchResults: SearchResult[] = [];
    const searchQuery = query.toLowerCase().trim();

    documents.forEach(doc => {
      if (!matchesTimeFilter(doc)) return;

      let score = 0;
      const matches: SearchResult['matches'] = {};
      let preview = '';

      // Search by category
      switch (category) {
        case 'name':
          score = fuzzyMatch(doc.name, searchQuery) * 2; // Boost name matches
          if (score > 0) matches.title = true;
          break;

        case 'content':
          if (doc.content && doc.content.toLowerCase().includes(searchQuery)) {
            score = 0.7;
            preview = extractPreview(doc.content, searchQuery);
            matches.content = [searchQuery];
          }
          break;

        case 'tags':
          if (!searchQuery.trim() && doc.tags && doc.tags.length > 0) {
            // Show all documents with tags when no query
            score = 0.7;
            matches.tags = doc.tags;
            preview = doc.tags.map(tag => `#${tag}`).join(' • ');
          } else if (doc.tags) {
            // Search for matching tags - exact substring match only
            const matchingTags = doc.tags.filter(tag => {
              const tagLower = tag.toLowerCase();
              const queryLower = searchQuery.toLowerCase();
              // Only exact substring match for tags (no fuzzy matching)
              return tagLower.includes(queryLower);
            });
            if (matchingTags.length > 0) {
              score = 0.8;
              matches.tags = matchingTags;
              preview = matchingTags.map(tag => `#${tag}`).join(' • ');
            }
          }
          break;

        case 'linked':
          // Search for documents that contain links matching the query
          if (doc.content) {
            const linkPattern = /\[\[(.*?)->\s*(.*?)\]\]/g;
            const matchingLinks: string[] = [];
            let match;
            
            while ((match = linkPattern.exec(doc.content)) !== null) {
              const linkText = match[1].trim();
              const linkTarget = match[2].trim();
              
              // If no query, show all docs with links
              if (!searchQuery.trim()) {
                matchingLinks.push(`[[${linkText} -> ${linkTarget}]]`);
              }
              // Check if link target contains search query (but not self-links)
              else if (linkTarget.toLowerCase().includes(searchQuery) && 
                       !doc.name.toLowerCase().includes(searchQuery)) {
                matchingLinks.push(`[[${linkText} -> ${linkTarget}]]`);
              }
            }
            
            if (matchingLinks.length > 0) {
              score = 0.8;
              // Create preview showing the actual links found
              preview = matchingLinks.slice(0, 3).join(' • ');
              if (matchingLinks.length > 3) {
                preview += ` • (+${matchingLinks.length - 3} more)`;
              }
            }
          }
          break;

        case 'recent':
          const days = (Date.now() - new Date(doc.modified).getTime()) / (1000 * 60 * 60 * 24);
          if (days < 7) {
            score = fuzzyMatch(doc.name, searchQuery) + (7 - days) / 14;
          }
          break;

        case 'modified':
          // Search by author or commit message
          if (doc.authors?.some(author => author.toLowerCase().includes(searchQuery))) {
            score = 0.6;
          }
          break;

        case 'all':
        default:
          // Search everywhere
          const nameScore = fuzzyMatch(doc.name, searchQuery) * 2;
          const contentScore = doc.content && doc.content.toLowerCase().includes(searchQuery) ? 0.7 : 0;
          const tagScore = (doc.tags || []).some(tag => 
            fuzzyMatch(tag, searchQuery) > 0.5
          ) ? 0.8 : 0;
          
          // Check for links in document
          let linkScore = 0;
          if (doc.content) {
            const linkPattern = /\[\[.*?->\s*(.*?)\]\]/g;
            const links: string[] = [];
            let match;
            while ((match = linkPattern.exec(doc.content)) !== null) {
              links.push(match[1].trim());
            }
            if (links.some(link => link.toLowerCase().includes(searchQuery))) {
              linkScore = 0.6;
            }
          }
          
          score = Math.max(nameScore, contentScore, tagScore, linkScore);
          
          if (nameScore > 0) matches.title = true;
          if (contentScore > 0 && doc.content) {
            matches.content = [searchQuery];
            preview = extractPreview(doc.content, searchQuery);
          }
          if (tagScore > 0) {
            matches.tags = (doc.tags || []).filter(tag => 
              fuzzyMatch(tag, searchQuery) > 0.5
            );
          }
          break;
      }

      if (score > 0.1) {
        searchResults.push({
          document: doc,
          score,
          matches,
          preview: preview || (doc.content ? doc.content.slice(0, 150) + '...' : '')
        });
      }
    });

    // Sort by score
    searchResults.sort((a, b) => b.score - a.score);
    setResults(searchResults.slice(0, 20)); // Limit to top 20 results
  }, [query, category, documents, matchesTimeFilter, fuzzyMatch, extractPreview]);

  // Search on query or filter change
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      performSearch();
    }, 150);

    return () => clearTimeout(debounceTimer);
  }, [query, category, timeFilter, performSearch]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev => Math.min(prev + 1, results.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => Math.max(prev - 1, 0));
          break;
        case 'Enter':
          e.preventDefault();
          if (results[selectedIndex]) {
            onSelectDocument(results[selectedIndex].document.path);
            onClose();
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, selectedIndex, results, onClose, onSelectDocument]);

  // Reset on open/close
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setSelectedIndex(0);
      setCategory('all');
      setTimeFilter('anytime');
    }
  }, [isOpen]);

  const categoryButtons: { key: SearchCategory; label: string; icon: string }[] = [
    { key: 'all', label: 'All', icon: '🔍' },
    { key: 'name', label: 'Name', icon: '📄' },
    { key: 'content', label: 'Content', icon: '📝' },
    { key: 'recent', label: 'Recent', icon: '🕐' },
    { key: 'linked', label: 'Linked', icon: '🔗' },
    { key: 'tags', label: 'Tags', icon: '🏷️' },
    { key: 'modified', label: 'History', icon: '📚' }
  ];

  const timeFilterOptions: { key: TimeFilter; label: string }[] = [
    { key: 'anytime', label: 'Any time' },
    { key: 'today', label: 'Today' },
    { key: 'week', label: 'This week' },
    { key: 'month', label: 'This month' },
    { key: 'year', label: 'This year' }
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div 
          className="search-modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div 
            className="search-modal"
            initial={{ y: -50, opacity: 0, scale: 0.95 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: -50, opacity: 0, scale: 0.95 }}
            transition={{ type: 'spring', damping: 20 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header with search input */}
            <div className="search-header">
              <div className="search-input-container">
                <span className="search-icon">🔍</span>
                <input
                  type="text"
                  className="search-input"
                  placeholder="Search documents, content, tags..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  autoFocus
                />
                {query && (
                  <button 
                    className="clear-button"
                    onClick={() => setQuery('')}
                  >
                    ✕
                  </button>
                )}
              </div>

              {/* Category filters */}
              <div className="category-filters">
                {categoryButtons.map(({ key, label, icon }) => (
                  <motion.button
                    key={key}
                    className={`category-button ${category === key ? 'active' : ''}`}
                    onClick={() => setCategory(key)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <span className="category-icon">{icon}</span>
                    <span className="category-label">{label}</span>
                  </motion.button>
                ))}
              </div>

              {/* Time filter */}
              <div className="time-filter">
                <span className="filter-label">Time:</span>
                {timeFilterOptions.map(({ key, label }) => (
                  <button
                    key={key}
                    className={`time-button ${timeFilter === key ? 'active' : ''}`}
                    onClick={() => setTimeFilter(key)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Search results */}
            <div className="search-results">
              {results.length === 0 && query && (
                <div className="no-results">
                  <span className="no-results-icon">🔍</span>
                  <p>No results found for "{query}"</p>
                  <p className="no-results-hint">Try different keywords or filters</p>
                </div>
              )}

              {results.length === 0 && !query && (
                <div className="search-welcome">
                  <h3>🏪 Document Search Kiosk</h3>
                  <p>Start typing to search across all your documents</p>
                  <div className="search-tips">
                    <p><strong>Tips:</strong></p>
                    <ul>
                      <li>Use category filters to narrow your search</li>
                      <li>Filter by time to find recent documents</li>
                      <li>Search by name, content, or tags</li>
                      <li>Use ↑↓ keys to navigate, Enter to select</li>
                    </ul>
                  </div>
                </div>
              )}

              {results.map((result, index) => (
                <motion.div
                  key={result.document.path}
                  className={`search-result ${index === selectedIndex ? 'selected' : ''}`}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.02 }}
                  onClick={() => {
                    onSelectDocument(result.document.path);
                    onClose();
                  }}
                  onMouseEnter={() => setSelectedIndex(index)}
                >
                  <div className="result-header">
                    <span className="result-name">
                      {result.matches.title ? (
                        <mark>{result.document.name}</mark>
                      ) : (
                        result.document.name
                      )}
                    </span>
                    <span className="result-score">
                      {Math.round(result.score * 100)}% match
                    </span>
                  </div>

                  {result.preview && (
                    <div className="result-preview">
                      {result.matches.content ? (
                        <span dangerouslySetInnerHTML={{
                          __html: result.preview.replace(
                            new RegExp(query, 'gi'),
                            match => `<mark>${match}</mark>`
                          )
                        }} />
                      ) : category === 'linked' && query ? (
                        <span dangerouslySetInnerHTML={{
                          __html: result.preview.replace(
                            new RegExp(query, 'gi'),
                            match => `<mark>${match}</mark>`
                          )
                        }} />
                      ) : (
                        result.preview
                      )}
                    </div>
                  )}

                  {result.matches.tags && result.matches.tags.length > 0 && (
                    <div className="result-tags">
                      {result.matches.tags.map(tag => (
                        <span key={tag} className="result-tag">
                          <mark>{tag}</mark>
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="result-meta">
                    <span className="result-path">{result.document.path}</span>
                    <span className="result-modified">
                      {new Date(result.document.modified).toLocaleDateString()}
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Footer */}
            <div className="search-footer">
              <div className="search-stats">
                {results.length > 0 && (
                  <span>{results.length} result{results.length !== 1 ? 's' : ''} found</span>
                )}
              </div>
              <div className="search-shortcuts">
                <span className="shortcut">↑↓ Navigate</span>
                <span className="shortcut">↵ Select</span>
                <span className="shortcut">ESC Close</span>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SearchModal;